import { createFileRoute } from "@tanstack/react-router";
import { Layout, Section, Eyebrow, FinalCTA, Breadcrumbs, CTAButton, BOOKING_URL } from "@/components/site/Layout";
import { breadcrumbSchema, jsonLdScript, organizationSchema } from "@/lib/seo";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About PageOne Lawyers | The SEO Agency Built for Attorneys" },
      { name: "description", content: "Why PageOne Lawyers exists, who we serve, and the principles that guide every engagement, built by SEOs who only work with law firms." },
      { property: "og:title", content: "About | PageOne Lawyers" },
      { property: "og:description", content: "The SEO agency built exclusively for law firms." },
      { property: "og:url", content: "https://pageonelawyers.com/about" },
    ],
    links: [{ rel: "canonical", href: "https://pageonelawyers.com/about" }],
    scripts: [
      jsonLdScript(organizationSchema()),
      jsonLdScript(breadcrumbSchema([
        { label: "Home", path: "/" },
        { label: "About", path: "/about" },
      ])),
    ],
  }),
  component: About,
});

function About() {
  const stats = [
    { n: "12 Years", l: "Exclusively legal SEO, no other verticals" },
  ];
  const team = [
    { n: "Priya Raman", r: "Lead SEO Strategist", b: "9 years in legal SEO. Ranked 40+ personal injury firms in the top-3 local pack across TX, AZ, and FL." },
    { n: "Marcus Delaney", r: "Technical SEO Lead", b: "Ex-enterprise SEO for multi-office firms. Owns Core Web Vitals, schema, and site architecture across every engagement." },
    { n: "Elena Cortés", r: "Content & Editorial Lead", b: "Former legal journalist. Manages attorney-reviewed content for criminal defense, immigration, and family practices." },
    { n: "Jordan Whitfield", r: "DUI & Criminal Defense Specialist", b: "8 years focused exclusively on high-competition criminal defense markets. Speaks at state bar CLE events on ethical advertising." },
    { n: "Amara Okafor", r: "Digital PR & Link Acquisition", b: "Places editorial coverage in ABA Journal, Law.com, and regional legal press. No PBNs, ever." },
    { n: "Sam Whitaker", r: "Analytics & Attribution Lead", b: "Builds the call-tracking, form-attribution, and dashboards that tie rankings to signed cases." },
  ];
  const pillars = [
    { t: "Ethical, white-hat only", d: "No PBNs, no spam links, no misleading ranking promises that put your bar license at risk." },
    { t: "Practice-area economics first", d: "We weight strategy by case value, not just search volume. A single high-value case can outweigh 1,000 low-intent clicks." },
    { t: "Built for how clients actually search", d: "Mobile-first, after-hours intent, 'near me' and city-modifier targeting, because legal emergencies don't happen at 10 AM." },
    { t: "AI-ready from day one", d: "Structured data and content built to get cited by ChatGPT, Gemini, and AI Overviews, not just rank on page one of classic Google." },
  ];
  const credentials = [
    "Familiar with state bar advertising rules across every market we serve",
    "Full client ownership of website, content, backlinks, and tracking data, no lock-in",
    "Transparent reporting: live dashboards and monthly plain-English summaries, not vague PDFs",
    "Registered business with a verifiable physical office and public leadership team",
  ];
  const testimonials = [
    { q: "We stopped burning $18k/month on Google Ads. Organic now delivers 70% of our signed cases.", n: "Managing Partner", f: "Multi-office trial firm", c: "Chicago, IL", pa: "Personal Injury" },
    { q: "They understand contingency-fee economics. Every content brief is tied to the case types we actually want.", n: "Founding Attorney", f: "Boutique litigation firm", c: "Phoenix, AZ", pa: "Personal Injury" },
    { q: "Ranked #1 in our county for our top three DUI keywords within 8 months. Reporting alone is worth the retainer.", n: "Equity Partner", f: "Criminal defense firm", c: "Houston, TX", pa: "DUI / Criminal Defense" },
    { q: "The only agency that actually reads our bar's advertising rules before publishing anything.", n: "Marketing Director", f: "Regional family law firm", c: "Boston, MA", pa: "Family Law" },
  ];
  const contrast = [
    "Exclusively legal since day one, not 'we also do law firms'",
    "Senior strategists on every account, not outsourced overflow work",
    "Practice-area specialization, not one-size-fits-all templates",
    "One firm per market, we won't take a competitor of yours in the same city",
  ];
  return (
    <Layout>
      {/* 1. Hero */}
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "About" }]} />
          <Eyebrow><span className="text-[var(--gold)]">About</span></Eyebrow>
          <h1 className="mt-6 font-display text-5xl md:text-6xl font-semibold max-w-4xl">We built PageOne Lawyers for the law firms tired of generalist agencies.</h1>
          <p className="mt-6 max-w-3xl text-lg text-primary-foreground/80 leading-relaxed">
            While generalist agencies split their attention across every industry, we've spent over a decade learning exactly one thing: how attorneys get found, get chosen, and get hired.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <CTAButton to="#team" variant="gold">Meet the team</CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">Talk to a strategist</CTAButton>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <div className="border-y border-border bg-background">
        <div className="mx-auto max-w-7xl px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.l} className="text-center md:text-left">
              <div className="font-display text-3xl md:text-4xl font-semibold text-foreground">{s.n}</div>
              <div className="mt-2 text-sm text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 2. Our story */}
      <Section>
        <Eyebrow>Our story</Eyebrow>
        <h2 className="mt-4 text-4xl font-semibold max-w-3xl">Why a legal-only SEO company exists</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-10 text-lg leading-relaxed text-muted-foreground">
          <div className="space-y-5">
            <p>Most SEO agencies work with restaurants on Monday, roofers on Tuesday, and a law firm on Wednesday. PageOne Lawyers doesn't.</p>
            <p>We started in 2013 with a simple observation: legal marketing is nothing like retail marketing. Client trust decisions happen before a single phone call. Bar association advertising rules restrict what you can even say. And the keywords that actually convert, "DUI lawyer near me at 2 AM," "personal injury attorney free consultation," require an entirely different playbook than ranking a product page.</p>
          </div>
          <div className="space-y-5">
            <p>Legal SEO is the only thing we do. Every strategist on our team has worked exclusively on legal SEO, not "marketing in general with some law firm clients."</p>
            <p>That distinction shapes how we plan, deliver and report on every engagement.</p>
          </div>
        </div>
      </Section>

      {/* Mid-page CTA */}
      <div className="bg-secondary/40 border-y border-border">
        <div className="mx-auto max-w-7xl px-6 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-2xl">
            <h3 className="text-2xl md:text-3xl font-semibold text-foreground">See what a legal-only team spots in your site.</h3>
            <p className="mt-2 text-muted-foreground">Free SEO audit built around your practice area and market, no generic checklist.</p>
          </div>
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            <CTAButton to="/law-firm-seo-audit" variant="gold">Get my free audit</CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">Book a call</CTAButton>
          </div>
        </div>
      </div>

      {/* 4. Leadership & team */}
      <Section>
        <div id="team" />
        <Eyebrow>Leadership & team</Eyebrow>
        <h2 className="mt-4 text-4xl font-semibold max-w-3xl">The strategists behind your rankings</h2>
        <p className="mt-4 max-w-3xl text-lg text-muted-foreground">A dedicated law firm needs a dedicated team, not a rotating pool of generalist freelancers. Every member below has spent their career in legal SEO.</p>
        <div className="mt-10 rounded-2xl border border-border bg-card p-8 flex flex-col sm:flex-row gap-8 items-start">
          <img
            src="/__l5e/assets-v1/49542b44-d142-44e5-b009-d029a898f033/aditya-chauhan.png"
            alt="Aditya Chauhan, Head of SEO at PageOne Lawyers"
            width={160}
            height={160}
            className="h-40 w-40 flex-shrink-0 rounded-2xl object-cover bg-secondary border border-border"
          />
          <div>
            <div className="text-2xl font-semibold text-foreground">Aditya Chauhan</div>
            <div className="text-sm text-[var(--gold-deep)]">Head of SEO, PageOne Lawyers</div>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">Aditya leads SEO strategy for every engagement at PageOne Lawyers, building search programs for law firms across personal injury, DUI, family, and criminal defense.</p>
          </div>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {team.map((m) => (
            <div key={m.n} className="rounded-xl border border-border bg-card p-6">
              <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[var(--gold)] to-[var(--gold-deep)] flex items-center justify-center text-primary-foreground font-semibold text-lg">
                {m.n.split(" ").map((p) => p[0]).slice(0, 2).join("")}
              </div>
              <div className="mt-4 text-lg font-semibold text-foreground">{m.n}</div>
              <div className="text-sm text-[var(--gold-deep)]">{m.r}</div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{m.b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 5. Approach / methodology */}
      <div className="bg-secondary/40 border-y border-border">
        <Section>
          <Eyebrow>Our approach</Eyebrow>
          <h2 className="mt-4 text-4xl font-semibold max-w-3xl">How we think differently about legal SEO</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {pillars.map((p) => (
              <div key={p.t} className="rounded-xl border border-border bg-background p-6">
                <div className="text-lg font-semibold text-foreground">{p.t}</div>
                <p className="mt-2 text-muted-foreground leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* 6. Credentials & compliance */}
      <Section>
        <Eyebrow>Credentials & compliance</Eyebrow>
        <h2 className="mt-4 text-4xl font-semibold max-w-3xl">Held to a higher standard</h2>
        <ul className="mt-8 grid md:grid-cols-2 gap-4">
          {credentials.map((c) => (
            <li key={c} className="flex gap-3 rounded-xl border border-border bg-card p-5">
              <span className="text-[var(--gold-deep)] font-bold">✓</span>
              <span className="text-foreground leading-relaxed">{c}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* 7. Results snapshot */}
      <div className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <Eyebrow><span className="text-[var(--gold)]">Results</span></Eyebrow>
          <h2 className="mt-4 text-4xl font-semibold max-w-3xl">12 years, measured in outcomes</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { n: "60–120 days", l: "Average time to first-page ranking" },
              { n: "38,000+", l: "Organic leads generated across active clients" },
              { n: "14", l: "Legal practice areas served" },
              { n: "27 states", l: "Active markets nationwide" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-3xl md:text-4xl font-semibold text-[var(--gold)]">{s.n}</div>
                <div className="mt-2 text-sm text-primary-foreground/80">{s.l}</div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <CTAButton to="/case-studies" variant="gold">See full case studies</CTAButton>
          </div>
        </div>
      </div>

      {/* 8. Testimonials */}
      <Section>
        <Eyebrow>What attorneys say</Eyebrow>
        <h2 className="mt-4 text-4xl font-semibold max-w-3xl">What attorneys say about working with us</h2>
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {testimonials.map((t) => (
            <figure key={t.q} className="rounded-xl border border-border bg-card p-6">
              <blockquote className="text-lg text-foreground leading-relaxed">&ldquo;{t.q}&rdquo;</blockquote>
              <figcaption className="mt-4 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{t.n}</span>, {t.f} &middot; {t.c} &middot; {t.pa}
              </figcaption>
            </figure>
          ))}
        </div>
      </Section>

      {/* 9. Values / what makes us different */}
      <div className="bg-secondary/40 border-y border-border">
        <Section>
          <Eyebrow>What makes us different</Eyebrow>
          <h2 className="mt-4 text-4xl font-semibold max-w-3xl">What you won't find with a generalist agency</h2>
          <ul className="mt-8 space-y-3">
            {contrast.map((c) => (
              <li key={c} className="flex gap-3 rounded-lg border border-border bg-background p-4">
                <span className="text-[var(--gold-deep)] font-bold">→</span>
                <span className="text-foreground">{c}</span>
              </li>
            ))}
          </ul>
        </Section>
      </div>

      <FinalCTA />
    </Layout>
  );
}
